/* =============================================================================
 * The language, held where anything can read it
 * -----------------------------------------------------------------------------
 * A module-level copy of the reader's language, with a getter and a setter and
 * nothing else. The same shape as the active timezone and the active date
 * format, and it used to live inside i18n.tsx beside the provider that sets it.
 *
 * It moved out here because of what importing it dragged along. The formatters
 * read it — dates, money, durations — and the export dictionary reads the
 * formatters, so `docLabel` in a report pulled in i18n.tsx, and i18n.tsx pulls
 * in React. Inside the browser that costs nothing and nobody notices.
 *
 * In an Edge Function it is fatal. The daily attendance email builds its report
 * with the same dictionary the screens use, deliberately, so the emailed day and
 * the downloaded one cannot disagree — and that import chain ended at React, in
 * a Deno runtime that has no business loading it. The deploy refused the whole
 * project, naming a file three hops from the one at fault.
 *
 * So the holder is here, where a calculator or a report builder can read the
 * language without a component library coming with it. Outside the browser it
 * simply stays 'en', which is what a server-rendered document should be unless
 * it was told otherwise.
 * ========================================================================== */

export type Language = 'en' | 'ar';

let active: Language = 'en';

/** The language anything outside React reads. */
export function currentLanguage(): Language {
  return active;
}

/**
 * Keeps the module-level copy in step with whoever owns the choice.
 *
 * The provider calls this during render rather than in an effect: a formatter
 * called while that render is still going reads the module copy, and an effect
 * would set it one paint too late — the first frame after a language change
 * would carry the old currency.
 */
export function setActiveLanguage(next: Language): void {
  active = next;
}
